import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  INVOICE_STATUS_MAP,
  QUOTATION_STATUS_MAP,
  tafqit,
  type Branding,
  type CompanyProfile,
  type TenantSettings,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { closePrintView } from '../../lib/printclose';
import { date, money } from '../../lib/format';
import { Logo } from '../../components/Logo';
import { PrintSheet, useDocumentPaper } from '../../components/DocumentPaper';
import '../../styles/print.css';

type DocKind = 'quotation' | 'invoice';

interface DocLine {
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  discount: string;
  lineTotal: string;
}

interface DocData {
  id: string;
  number: string;
  status: string;
  title: string | null;
  notes: string | null;
  terms: string | null;
  issuedAt: string;
  validUntil?: string | null;
  dueAt?: string | null;
  subtotal: string;
  discount: string;
  taxRate: string;
  taxAmount: string;
  total: string;
  paidAmount?: string;
  lines: DocLine[];
  account: {
    id: string;
    name: string;
    legalName: string | null;
    taxNumber: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
  } | null;
}

interface Profile {
  tenant: { name: string; currency: string };
  branding: Branding;
  company: CompanyProfile;
  settings: TenantSettings;
}

const DOC_TITLES: Record<DocKind, string> = {
  quotation: 'عرض سعر',
  invoice: 'فاتورة',
};

/**
 * المستند المطبوع.
 *
 * الطباعة من المتصفح لا توليد PDF في الخادم. السبب أن العربية تحتاج تشكيل
 * الحروف واتجاهًا ثنائيًا، ومكتبات PDF في Node تعرض الحروف منفصلة أو معكوسة.
 * المتصفح يرسمها صحيحة أصلًا، و"حفظ كـ PDF" يعطي ملفًا مطابقًا لما يُعرض،
 * بلا خط إضافي على الخادم ولا اعتماد على Chromium هناك.
 */
export function PrintDocument() {
  const { kind = 'quotation', id = '' } = useParams<{ kind: DocKind; id: string }>();
  const [params] = useSearchParams();
  const autoPrint = params.get('print') === '1';

  const path = kind === 'invoice' ? `/finance/invoices/${id}` : `/sales/quotations/${id}`;

  const doc = useQuery({
    queryKey: ['print-doc', kind, id],
    queryFn: () => api<DocData>(path),
  });

  const profile = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<Profile>('/settings'),
  });
  /* ورقةُ الشركة (ليتر هيد) إن رُفعت: تحلّ محلّ ترويسة النظام وتذييله. الفاتورةُ على ورقتها إن رُفعت، وعرضُ السعر على العامّة. */
  const { paper, ready: paperReady } = useDocumentPaper(kind === 'invoice' ? 'invoice' : 'letterhead');

  const ready = doc.data && profile.data && paperReady;

  /*
   * الطباعة تنتظر اكتمال البيانات.
   *
   * فتح نافذة الطباعة قبلها يطبع صفحة فارغة، وهو ما يحدث لو استُدعيت
   * الطباعة عند تحميل المكوّن بدل انتظار الاستعلامات.
   */
  useEffect(() => {
    if (!autoPrint || !ready) return;
    const timer = setTimeout(() => window.print(), 400);
    return () => clearTimeout(timer);
  }, [autoPrint, ready]);

  if (doc.isError || profile.isError) {
    return (
      <div className="print-page">
        <p>تعذّر تحميل المستند.</p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="print-page">
        <p>جارٍ التحضير…</p>
      </div>
    );
  }

  const { branding, company, settings, tenant } = profile.data;
  const d = doc.data;
  const isInvoice = kind === 'invoice';

  const statusLabel = isInvoice
    ? INVOICE_STATUS_MAP[d.status as keyof typeof INVOICE_STATUS_MAP]?.label
    : QUOTATION_STATUS_MAP[d.status as keyof typeof QUOTATION_STATUS_MAP]?.label;

  const currency = settings.currencyLabel || 'ج.م';
  const remaining = isInvoice ? Number(d.total) - Number(d.paidAmount ?? 0) : 0;

  return (
    <div
      className="print-root"
      style={{
        ['--doc-primary' as string]: branding.primary,
        ['--doc-ink' as string]: branding.ink,
      }}
    >
      <div className="print-toolbar no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          طباعة أو حفظ PDF
        </button>
        <button className="btn" onClick={closePrintView}>
          إغلاق
        </button>
        <span className="small mute">
          اختر «حفظ كـ PDF» في نافذة الطباعة للحصول على ملف.
        </span>
      </div>

      <PrintSheet paper={paper}>
        {/* الترويسة */}
        <header className="doc-header">
          <div className="doc-brand">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="doc-logo" />
            ) : (
              <Logo size={52} />
            )}
            <div>
              <div className="doc-company">{company.legalName || tenant.name}</div>
              {branding.documentHeader && (
                <div className="doc-tagline">{branding.documentHeader}</div>
              )}
              <div className="doc-company-meta">
                {company.address && <div>{company.address}</div>}
                <div className="doc-inline">
                  {company.phone && <span className="num">{company.phone}</span>}
                  {company.email && <span className="num">{company.email}</span>}
                  {company.website && <span className="num">{company.website}</span>}
                </div>
                {branding.showTaxNumber && company.taxNumber && (
                  <div>
                    الرقم الضريبي: <span className="num">{company.taxNumber}</span>
                    {company.commercialRegister && (
                      <>
                        {' — '}س.ت: <span className="num">{company.commercialRegister}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="doc-title-block">
            <div className="doc-kind">{DOC_TITLES[kind as DocKind]}</div>
            <div className="doc-number num">{d.number}</div>
            {statusLabel && <div className="doc-status">{statusLabel}</div>}
          </div>
        </header>

        {/* بيانات الطرفين والتواريخ */}
        <section className="doc-parties">
          <div className="doc-party">
            <div className="doc-label">مقدَّم إلى</div>
            <div className="doc-party-name">{d.account?.legalName || d.account?.name}</div>
            {d.account?.address && <div>{d.account.address}</div>}
            <div className="doc-inline">
              {d.account?.phone && <span className="num">{d.account.phone}</span>}
              {d.account?.email && <span className="num">{d.account.email}</span>}
            </div>
            {d.account?.taxNumber && (
              <div>
                الرقم الضريبي: <span className="num">{d.account.taxNumber}</span>
              </div>
            )}
          </div>

          <div className="doc-dates">
            <div className="doc-date-row">
              <span className="doc-label">تاريخ الإصدار</span>
              <span>{date(d.issuedAt)}</span>
            </div>
            {isInvoice ? (
              <div className="doc-date-row">
                <span className="doc-label">تاريخ الاستحقاق</span>
                <span>{date(d.dueAt)}</span>
              </div>
            ) : (
              <div className="doc-date-row">
                <span className="doc-label">صالح حتى</span>
                <span>{date(d.validUntil)}</span>
              </div>
            )}
            {d.title && (
              <div className="doc-date-row">
                <span className="doc-label">الموضوع</span>
                <span>{d.title}</span>
              </div>
            )}
          </div>
        </section>

        {/* البنود */}
        <table className="doc-table">
          <thead>
            <tr>
              <th style={{ width: 34 }}>م</th>
              <th>البيان</th>
              <th style={{ width: 70 }}>الكمية</th>
              <th style={{ width: 70 }}>الوحدة</th>
              <th style={{ width: 100 }}>السعر</th>
              <th style={{ width: 90 }}>الخصم</th>
              <th style={{ width: 110 }}>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            {d.lines.map((line, index) => (
              <tr key={index}>
                <td className="num center">{index + 1}</td>
                <td>{line.description}</td>
                <td className="num center">{Number(line.quantity)}</td>
                <td className="center">{line.unit}</td>
                <td className="num end">{money(line.unitPrice, currency)}</td>
                <td className="num end">
                  {Number(line.discount) ? money(line.discount, currency) : '—'}
                </td>
                <td className="num end strong">{money(line.lineTotal, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* الإجماليات */}
        <section className="doc-summary" data-keep-with-prev>
          <div className="doc-words">
            <div className="doc-label">المبلغ بالحروف</div>
            {/*
              التفقيط مطلوب على المستندات الرسمية: تغيير رقم سهل، وتغيير
              جملة مكتوبة ليس كذلك.
            */}
            <div className="doc-words-text">{tafqit(Number(d.total), tenant.currency)}</div>
          </div>

          <table className="doc-totals">
            <tbody>
              <tr>
                <td>المجموع</td>
                <td className="num end">{money(d.subtotal, currency)}</td>
              </tr>
              {Number(d.discount) > 0 && (
                <tr>
                  <td>الخصم</td>
                  <td className="num end">{money(d.discount, currency)}</td>
                </tr>
              )}
              <tr>
                <td>
                  ضريبة القيمة المضافة <span className="num">({Number(d.taxRate)}٪)</span>
                </td>
                <td className="num end">{money(d.taxAmount, currency)}</td>
              </tr>
              <tr className="doc-total-row">
                <td>الإجمالي المستحق</td>
                <td className="num end">{money(d.total, currency)}</td>
              </tr>
              {isInvoice && Number(d.paidAmount ?? 0) > 0 && (
                <>
                  <tr>
                    <td>المحصَّل</td>
                    <td className="num end">{money(d.paidAmount ?? 0, currency)}</td>
                  </tr>
                  <tr className="doc-total-row">
                    <td>المتبقي</td>
                    <td className="num end">{money(remaining, currency)}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </section>

        {/* الشروط والملاحظات */}
        {(d.terms || d.notes) && (
          <section className="doc-terms">
            {d.terms && (
              <div>
                <div className="doc-label">الشروط</div>
                <div className="doc-text">{d.terms}</div>
              </div>
            )}
            {d.notes && (
              <div>
                <div className="doc-label">ملاحظات</div>
                <div className="doc-text">{d.notes}</div>
              </div>
            )}
          </section>
        )}

        {/* بيانات التحويل */}
        {isInvoice && branding.showBankDetails && company.bankName && (
          <section className="doc-bank">
            <div className="doc-label">بيانات التحويل البنكي</div>
            <div className="doc-inline">
              <span>{company.bankName}</span>
              {company.bankAccount && (
                <span>
                  رقم الحساب: <span className="num">{company.bankAccount}</span>
                </span>
              )}
              {company.bankIban && (
                <span>
                  الآيبان: <span className="num">{company.bankIban}</span>
                </span>
              )}
            </div>
            <div className="tiny">يُرجى ذكر رقم المستند عند التحويل.</div>
          </section>
        )}

        {/* التذييل والختم */}
        <footer className="doc-footer">
          <div className="doc-footer-text">{branding.documentFooter}</div>
          <div className="doc-signature">
            {branding.stampUrl ? (
              <img src={branding.stampUrl} alt="" className="doc-stamp" />
            ) : (
              <div className="doc-sign-line" />
            )}
            <div className="tiny">التوقيع والختم</div>
          </div>
        </footer>
      </PrintSheet>
    </div>
  );
}
